import type { PaletteColor, SxProps } from '@mui/material';
import {
  Box,
  Image,
  Grid,
  Card,
  Typography,
  Stack,
  Divider,
  useDeviceType,
  Flag,
  Link,
  useFormatPrice,
  useI18n,
} from '@packages/shared';
import React from 'react';
import { FormattedMessage, useIntl, defineMessages } from 'react-intl';
import { type FragmentType, getFragmentData } from '@packages/gql/generated/shopping';
import type { LineItemFragmentFragment } from '@packages/gql/generated/shopping/graphql';
import { LineItemFragmentFragmentDoc } from '@packages/gql/generated/shopping/LineItemFragmentFragmentDoc';
import type { GlycerinCartProduct } from '@packages/tracking';
import { MiniDVLayer } from '@packages/modules';
import { MoreMenu } from '../MoreMenu';
import { ActionBar } from '../ActionBar';
import { Aplus } from '../Aplus';
import { AddToWishlistAction, DeleteAction, EditAction } from '../Actions';
import { PowerEfficiency } from './PowerEfficiency';
import { ProductPriceWrapper } from './ProductPriceWrapper';
import { buildGlycerinCartProduct } from '../Tracking/buildGlycerinObject';

const LineItemCard = (lastItem: LineItemProps['lastItem']): SxProps => ({
  width: '100%',
  pt: 1.5,
  overflow: 'visible',
  position: 'relative',
  mb: lastItem
    ? 0
    : {
        xs: 1.5,
        md: 2,
      },
});
const MainProductContainer: SxProps = {
  flexDirection: { xs: 'column', md: 'row' },
  flexWrap: { xs: 'wrap', md: 'nowrap' },
  flexGrow: 1,
  gap: { xs: 1.5 },
};
const ProductTextContainer: SxProps = {
  pb: { xs: '1.125rem', md: 1 },
  mr: { xs: 5, md: '0' },
};

const ImageContainer: SxProps = {
  position: 'relative',
  display: 'flex',
  flexFlow: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: { xs: '80px', md: '120px' },
  width: { xs: '80px', md: '120px' },
  my: 2,
  '> img': {
    objectFit: 'contain',
  },
  '> div.image-expand-container': {
    position: 'absolute',
    display: 'none',
    zIndex: 1,
    height: { xs: '80px', md: '120px' },
    width: { xs: '80px', md: '120px' },
    transform: 'scale(1) translateX(0)',
    '> img': {
      objectFit: 'contain',
    },
  },
  ':hover > div.image-expand-container': {
    display: 'flex',
    transform: 'scale(3.5) translateX(3.5rem)',
    zIndex: 1,
  },
};
const SustainedFlag: SxProps = {
  position: 'absolute',
  top: '10px',
  left: '0',
  overflow: 'hidden',
};

const messages = defineMessages({
  lineItemConfigurationSERIALNUMBER: {
    id: 'LineItem.Configuration.SerialNumber',
    defaultMessage: 'Bedienelement / Seriennummer: {serialNumber}',
  },
  lineItemConfigurationWIDTH: {
    id: 'LineItem.Configuration.Width',
    defaultMessage: 'Breite: {width} cm',
  },
  lineItemConfigurationLENGTH: {
    id: 'LineItem.Configuration.Length',
    defaultMessage: 'Länge: {length} cm',
  },
  lineItemConfigurationENGRAVING: {
    id: 'LineItem.Configuration.Engraving',
    defaultMessage: 'Ihr Gravurtext: {engraving}',
  },
  sustainedFlag: {
    id: 'LineItem.SustainedFlag',
    defaultMessage: 'Nachhaltig',
  },
});

const availabilityStateColors: Record<
  LineItemFragmentFragment['availability']['state'],
  PaletteColor['main']
> = {
  AVAILABLE: 'success.main',
  NOT_AVAILABLE: 'error.main',
  NOT_YET_AVAILABLE: 'warning.main',
  UNSELECTED: 'primary.main',
};

type LineItemActionsProps = {
  sku: LineItemFragmentFragment['product']['sku'];
  lineItemId: LineItemFragmentFragment['id'];
  glycerinProduct: GlycerinCartProduct;
  quantity: LineItemFragmentFragment['quantity'];
  akl: LineItemFragmentFragment['product']['akl'];
};

const LineItemActions: React.FC<LineItemActionsProps> = ({
  lineItemId,
  sku,
  glycerinProduct,
  quantity,
  akl,
}) => {
  const { isMobile } = useDeviceType();
  const [isMiniDVOpen, setIsMiniDVOpen] = React.useState(false);
  const children = [
    <EditAction
      mode="basket"
      key="edit"
      glycerinProduct={glycerinProduct}
      onClick={() => setIsMiniDVOpen(true)}
    />,
    <AddToWishlistAction
      key="wishlist"
      sku={sku}
      lineItemId={lineItemId}
      glycerinProduct={glycerinProduct}
    />,
    <DeleteAction
      mode="basket"
      key="delete"
      lineItemId={lineItemId}
      glycerinProduct={glycerinProduct}
    />,
  ];

  const Wrapper = isMobile ? MoreMenu : ActionBar;

  return (
    <>
      <Wrapper>{isMobile ? children : children.reverse()}</Wrapper>
      {lineItemId && (
        <MiniDVLayer
          sku={sku}
          akl={akl}
          lineItemId={lineItemId}
          quantity={quantity}
          open={isMiniDVOpen}
          onClose={() => setIsMiniDVOpen(false)}
          mode="ATB"
        />
      )}
    </>
  );
};

/* GraphQL */ `
  fragment LineItemFragment on BasketLineItem {
    id
    product {
      akl
      sku
      orderNumber
      imageUrl
      name
      brand
      powerEfficiencyFlags {
        text
        iconText
        link
        icon
        dataSheet
      }
      variation
      sustained
    }
    price {
      amount
      currency
      isStartPrice
      saving {
        type
        value
      }
      unit
      uvp
      value
      valueOld
      valueUnit
      discount {
        discountAmount
        discountPercentage
        amountBeforeDiscount
      }
    }
    singlePrice {
      amount
      currency
    }
    availability {
      message
      state
    }
    configuration {
      length
      width
      engraving
      serialNumber
    }
    aplus {
      ...AplusFragment
    }
    quantity
    isSoldOut
  }
`;

type LineItemProps = {
  lineItemData: FragmentType<typeof LineItemFragmentFragmentDoc>;
  lastItem: boolean;
};

export const LineItem = ({ lineItemData, lastItem = false }: LineItemProps) => {
  const fragmentData = getFragmentData(LineItemFragmentFragmentDoc, lineItemData);
  const { formatPrice } = useFormatPrice();
  const {
    availability,
    configuration,
    id,
    isSoldOut,
    price,
    product,
    quantity,
    singlePrice,
    aplus,
  } = fragmentData;
  const glycerinProduct = buildGlycerinCartProduct(fragmentData);

  const { language, localizeUrl } = useI18n();
  const { formatMessage } = useIntl();
  const { isMobile } = useDeviceType();
  return (
    <Card sx={LineItemCard(lastItem)}>
      <Grid container pb={1.5} flexWrap="nowrap" overflow="visible">
        <Grid item ml="0.75rem" overflow="visible">
          <Link sx={ImageContainer} href={localizeUrl(`/p/${product.sku}`, language)}>
            <Image priority unoptimized alt={product.name} src={product.imageUrl} fill />
            {product.sustained && (
              <Flag
                text={formatMessage(messages.sustainedFlag)}
                sx={SustainedFlag}
                type="sustainability"
              />
            )}
            {!isMobile && (
              <Box className="image-expand-container">
                <Image priority unoptimized alt={product.name} src={product.imageUrl} fill />
              </Box>
            )}
          </Link>
        </Grid>
        <Grid item xs container sx={{ ml: { xs: 1.5 }, mr: { xs: 1.5 } }}>
          <Grid item container sx={MainProductContainer}>
            <Grid item flexGrow={1}>
              <Box sx={ProductTextContainer}>
                <Typography
                  variant={isMobile ? 'body2' : 'body1'}
                  component="h4"
                  fontWeight="fontWeightSemiBold"
                  sx={{ mb: { xs: '0', md: 1 } }}
                >
                  {product.name}
                </Typography>
                <Typography variant="body2">{product.variation}</Typography>
                {configuration &&
                  Object.entries(configuration).map(
                    ([key, value]) =>
                      key !== '__typename' &&
                      value && (
                        <Typography variant="body2" color="text.secondary">
                          {formatMessage(
                            messages[
                              `lineItemConfiguration${key.toUpperCase()}` as keyof typeof messages
                            ],
                            { [key]: value },
                          )}
                        </Typography>
                      ),
                  )}
              </Box>
              <Typography variant="body2" pb="0.25rem" display="inline-block">
                <FormattedMessage
                  id="LineItem.Quantity"
                  defaultMessage="Anzahl: {quantity}"
                  values={{
                    quantity,
                  }}
                />
              </Typography>
              {singlePrice && singlePrice.amount && (
                <Typography ml={1} variant="inherit" display="inline-block">
                  <FormattedMessage
                    id="LineItem.SinglePrice"
                    defaultMessage=" (à {singlePrice})"
                    values={{
                      singlePrice: formatPrice(
                        singlePrice.amount,
                        singlePrice.currency satisfies string,
                      ),
                    }}
                  />
                </Typography>
              )}
              <Typography variant="body2" color={availabilityStateColors[availability.state]}>
                {availability.message}
              </Typography>
              <Grid container direction="column" pt={1}>
                <PowerEfficiency efficiency={product.powerEfficiencyFlags} />
              </Grid>
            </Grid>
            <ProductPriceWrapper isSoldOut={isSoldOut} price={price} />
          </Grid>
        </Grid>
      </Grid>
      <Stack mt={0.5}>
        {/* It seems like we no longer have messages in our product data this needs to be clarified if we still need this
        {serverMessages &&
          serverMessages.map((serverMessage) => (
            <InlineNotification
              severity={serverMessage.level.toLowerCase() as Lowercase<BasketMessageLevel>}
              sx={{
                maxWidth: '100%',
                height: { xs: '3rem', md: '1.875rem' },
                mb: { xs: '0.75rem', md: '0.5rem' },
              }}
            >
              {serverMessage.description}
            </InlineNotification>
          ))} */}
        {/* ------- APlus Section ------- */}
        {!!aplus && (
          <>
            <Divider />
            <Aplus aplusData={aplus} lineItemId={id!} quantity={quantity} />
          </>
        )}
      </Stack>
      <LineItemActions
        sku={product.sku}
        lineItemId={id}
        glycerinProduct={glycerinProduct}
        akl={product.akl}
        quantity={quantity}
      />
    </Card>
  );
};
